import React, { useEffect, useMemo, useState } from 'react';
import { Matiere } from '../../pages/Devis/ConfigurateurArticle/types';
import { matiereService } from '../../services/MatiereService';
import FournisseursSection from './FournisseursSection';
import MatiereModal, { MatiereModalFormData } from './MatiereModal';

const PAGE_SIZE = 10;

const MatieresList: React.FC = () => {
  const [matieres, setMatieres] = useState<Matiere[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [usageFilter, setUsageFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showMatiereModal, setShowMatiereModal] = useState(false);
  const [selectedMatiere, setSelectedMatiere] = useState<Matiere | null>(null);

  const typesMatiere = [
    { id: 'similicuir', label: 'Similicuir' },
    { id: 'tep', label: 'TEP' },
    { id: 'mesh', label: 'Mesh (grille)' },
    { id: 'tissu', label: 'Tissu' },
    { id: 'bache', label: 'Bâche' },
  ];

  const loadMatieres = async () => {
    try {
      setLoading(true);
      const data = await matiereService.getMatieres();
      setMatieres(data);
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatieres();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, usageFilter]);

  const handleSaveMatiere = async (data: MatiereModalFormData) => {
    try {
      setLoading(true);
      await matiereService.saveMatiere({
        ...selectedMatiere,
        ...data,
        id: selectedMatiere?.id || data.id || '',
      } as Matiere);
      await loadMatieres();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la matière:", error);
    } finally {
      setLoading(false);
      setShowMatiereModal(false);
      setSelectedMatiere(null);
    }
  };

  const supprimerMatiere = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette matière ?')) {
      try {
        setLoading(true);
        await matiereService.deleteMatiere(id);
        await loadMatieres();
      } catch (error) {
        console.error('Erreur lors de la suppression de la matière:', error);
        alert('Une erreur est survenue lors de la suppression de la matière');
      } finally {
        setLoading(false);
      }
    }
  };

  const mapCouleurs = (matiere: any) =>
    (matiere as any)._couleursNormalisees
      ? (matiere as any)._couleursNormalisees
      : (matiere.couleurs || []).map((c: any) => (typeof c === 'string' ? { nom: c, hex: '' } : { nom: c?.nom || '', hex: c?.hex || '' }));

  const filteredMatieres = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    return matieres.filter((matiere) => {
      const matchesSearch =
        !lowerSearch ||
        matiere.nom.toLowerCase().includes(lowerSearch) ||
        mapCouleurs(matiere).some((c: any) => (c.nom || '').toLowerCase().includes(lowerSearch));

      const matchesType = !typeFilter || matiere.type === typeFilter;

      const matchesUsage =
        !usageFilter ||
        (usageFilter === 'indoor' && matiere.usage_indoor) ||
        (usageFilter === 'outdoor' && matiere.usage_outdoor) ||
        (usageFilter === 'nautisme' && matiere.usage_nautisme) ||
        (usageFilter === 'medical' && matiere.usage_medical);

      return matchesSearch && matchesType && matchesUsage;
    });
  }, [matieres, search, typeFilter, usageFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredMatieres.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const start = (page - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const paginatedMatieres = filteredMatieres.slice(start, end);

  const formatPrix = (prix: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(prix);
  };

  const pageNumbers = useMemo<(number | string)[]>(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (page <= 3) {
      return [1, 2, 3, 4, '...', totalPages];
    }

    if (page >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, '...', page - 1, page, page + 1, '...', totalPages];
  }, [page, totalPages]);

  if (loading && matieres.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des matières...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <FournisseursSection />

      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h2 className="text-xl font-medium text-gray-900">Catalogue matières</h2>
          <div className="flex gap-3">
            <button className="btn-primary" onClick={() => loadMatieres()}>
              Rafraîchir la liste
            </button>
            <button
              className="btn-primary flex items-center gap-2"
              onClick={() => {
                setSelectedMatiere(null);
                setShowMatiereModal(true);
              }}
            >
              + Nouvelle matière
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="form-label">Recherche matière ou couleur</label>
              <input
                type="text"
                className="input-field"
                placeholder="Rechercher par nom ou couleur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Type</label>
              <select
                className="input-field"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Tous les types</option>
                {typesMatiere.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Usage</label>
              <select
                className="input-field"
                value={usageFilter}
                onChange={(e) => setUsageFilter(e.target.value)}
              >
                <option value="">Tous les usages</option>
                <option value="indoor">Indoor</option>
                <option value="outdoor">Outdoor</option>
                <option value="nautisme">Nautisme</option>
                <option value="medical">Médical</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button
              type="button"
              className="text-sm text-primary-600 hover:text-primary-800"
              onClick={() => {
                setSearch('');
                setTypeFilter('');
                setUsageFilter('');
              }}
            >
              Réinitialiser
            </button>
          </div>
        </div>

        <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Laize</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remise</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Min. Commande
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Couleurs</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedMatieres.map((matiere) => {
                const colors = mapCouleurs(matiere);
                const visibleColors = colors.slice(0, 2);
                const remainingColors = Math.max(colors.length - visibleColors.length, 0);
                return (
                  <tr key={matiere.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{matiere.nom}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {typesMatiere.find((t) => t.id === matiere.type)?.label || matiere.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{matiere.laize} cm</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatPrix(matiere.prix)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {matiere.remiseFournisseur ? `${matiere.remiseFournisseur}%` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {matiere.minimumCommande ? `${matiere.minimumCommande} ML` : '1 ML'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex gap-2">
                        {matiere.usage_indoor && <span title="Indoor">🏠</span>}
                        {matiere.usage_outdoor && <span title="Outdoor">⛱️</span>}
                        {matiere.usage_nautisme && <span title="Nautisme">⛵</span>}
                        {matiere.usage_medical && <span title="Médical">🩺</span>}
                        {!matiere.usage_indoor &&
                          !matiere.usage_outdoor &&
                          !matiere.usage_nautisme &&
                          !matiere.usage_medical && <span className="text-gray-400 text-xs">Non défini</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div
                        className="max-h-16 overflow-hidden"
                        title={colors.map((c: any) => c.nom).join(', ')}
                      >
                        <div className="flex flex-col gap-1">
                          {visibleColors.map((couleur: any, idx: number) => (
                            <span key={`${couleur.nom}-${idx}`} className="text-gray-700 flex items-center gap-2">
                              <span
                                className="w-3 h-3 rounded-full border border-gray-300 inline-block"
                                style={{
                                  backgroundColor: couleur.hex || 'transparent',
                                  borderColor: couleur.hex ? 'transparent' : '#d1d5db',
                                }}
                                title={couleur.hex || 'Aucune couleur définie'}
                              />
                              {couleur.nom}
                            </span>
                          ))}
                          {remainingColors > 0 && (
                            <span className="text-primary-600 font-medium">+{remainingColors} autres</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-primary-600 hover:text-primary-900 mr-3"
                        onClick={() => {
                          setSelectedMatiere(matiere);
                          setShowMatiereModal(true);
                        }}
                      >
                        Modifier
                      </button>
                      <button className="text-red-600 hover:text-red-900" onClick={() => supprimerMatiere(matiere.id)}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                );
              })}
              {paginatedMatieres.length === 0 && (
                <tr>
                  <td className="px-6 py-4 text-center text-sm text-gray-500" colSpan={9}>
                    Aucune matière trouvée avec ces filtres.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-container flex items-center justify-between mt-4">
          <div className="text-sm text-gray-600">
            {filteredMatieres.length === 0
              ? '0–0 sur 0 matières'
              : `${start + 1}–${Math.min(end, filteredMatieres.length)} sur ${filteredMatieres.length} matières`}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            >
              Précédent
            </button>
            {pageNumbers.map((num, index) =>
              typeof num === 'string' ? (
                <span key={`${num}-${index}`} className="px-2 text-gray-400">
                  {num}
                </span>
              ) : (
                <button
                  key={num}
                  className={`px-3 py-1 rounded-md text-sm ${
                    num === page
                      ? 'bg-primary-600 text-white border border-primary-600'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setPage(num)}
                >
                  {num}
                </button>
              )
            )}
            <button
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              Dernière
            </button>
            <button
              className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
              disabled={page === totalPages}
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      <MatiereModal
        open={showMatiereModal}
        onClose={() => {
          setShowMatiereModal(false);
          setSelectedMatiere(null);
        }}
        matiere={selectedMatiere}
        onSave={handleSaveMatiere}
        typesMatiere={typesMatiere}
      />
    </div>
  );
};

export default MatieresList;
